// src/App.js
import React, { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";

import Landing from "./pages/Landing";
import Home from "./pages/Home";
import Journal from "./pages/Journal";
import Goals from "./pages/Goals";
import Planner from "./pages/Planner";
import Streaks from "./pages/Streaks";
import Profile from "./pages/Profile";
import Premium from "./pages/Premium";
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import Success from "./pages/Success";
import Cancel from "./pages/Cancel";
import Updates from "./pages/Updates";
import ManageSubscription from "./pages/ManageSubscription";
import About from "./pages/About";

import { AuthProvider, useAuth } from "./context/AuthContext";

import "./styles.css";
import "./mobile.css";

/* ---------------- PRIVATE ROUTE ---------------- */
function PrivateRoute({ children }) {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" replace />;
}

/* ---------------- APP CONTENT ---------------- */
function AppContent() {
  const { user } = useAuth();

  useEffect(() => {
    const savedTheme = localStorage.getItem("rr_theme") || "classic-purple";
    document.documentElement.setAttribute("data-theme", savedTheme);
  }, []);

  return (
    <Routes>
      {/* ---------- PUBLIC ROUTES ---------- */}
      <Route path="/" element={user ? <Navigate to="/journal" replace /> : <Landing />} />
      <Route path="/login" element={user ? <Navigate to="/journal" replace /> : <Login />} />
      <Route path="/about" element={<About />} />

      {/* ---------- LOGGED-IN APP ---------- */}
      {user && (
        <Route
          path="*"
          element={
            <div className="layout">
              <Sidebar />
              <div className="content">
                <Routes>
                  {/* FORCE JOURNAL AS HOME */}
                  <Route path="/" element={<Navigate to="/journal" replace />} />

                  <Route path="/home" element={<PrivateRoute><Home /></PrivateRoute>} />
                  <Route path="/journal" element={<PrivateRoute><Journal /></PrivateRoute>} />
                  <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                  <Route path="/goals" element={<PrivateRoute><Goals /></PrivateRoute>} />
                  <Route path="/planner" element={<PrivateRoute><Planner /></PrivateRoute>} />
                  <Route path="/streaks" element={<PrivateRoute><Streaks /></PrivateRoute>} />
                  <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
                  <Route path="/premium" element={<PrivateRoute><Premium /></PrivateRoute>} />
                  <Route path="/updates" element={<PrivateRoute><Updates /></PrivateRoute>} />
                  <Route path="/success" element={<PrivateRoute><Success /></PrivateRoute>} />
                  <Route path="/cancel" element={<PrivateRoute><Cancel /></PrivateRoute>} />
                  <Route
                    path="/manage-subscription"
                    element={<PrivateRoute><ManageSubscription /></PrivateRoute>}
                  />

                  {/* FALLBACK */}
                  <Route path="*" element={<Navigate to="/journal" replace />} />
                </Routes>
              </div>
            </div>
          }
        />
      )}

      {/* ---------- LOGGED-OUT FALLBACK ---------- */}
      {!user && <Route path="*" element={<Navigate to="/" replace />} />}
    </Routes>
  );
}

/* ---------------- ROOT ---------------- */
export default function App() {
  useEffect(() => {
    const handler = () => {
      window.location.href = "/login";
    };
    window.addEventListener("rr:logout", handler);
    return () => window.removeEventListener("rr:logout", handler);
  }, []);

  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
